import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PETROL = [12, 43, 56];
const TEAL = [14, 165, 183];
const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const distNum = (d) => { if (typeof d === "number") return d; const m = String(d ?? "").match(/[\d.]+/); return m ? parseFloat(m[0]) : 0; };

// Fórmulas aplicadas por el motor COREX, por familia (transparencia total de ingeniería).
const FORMULAS = {
  zapatas: [
    "Concreto (m³) = Largo × Ancho × Alto × Cantidad",
    "Acero (kg) = Σ (n_barras × L_efectiva × peso_ø) × 2 mallas × (1 + 5% desperdicio)",
    "n_barras se distribuye en malla X-X y Y-Y; L_efectiva = dimensión − 2·recubrimiento",
  ],
  viga_cimentacion: [
    "Concreto (m³) = Largo × Ancho × Alto × Cantidad",
    "Acero longitudinal (kg) = Σ (n × Largo × peso_ø)  ·  Estribos = n × perímetro × peso_ø",
    "Acero total = (longitudinal + estribos) × (1 + 5% desperdicio)",
  ],
  cimiento_corrido: ["Concreto (m³) = Largo × Ancho × Alto × Cantidad", "Elemento de concreto simple (sin acero)"],
  sobrecimiento: [
    "Concreto (m³) = Largo × Ancho × Alto × Cantidad",
    "Acero (kg) = malla X-X + Y-Y sobre longitud efectiva × peso_ø × (1 + 5%)",
  ],
  columnas: [
    "Concreto (m³) = Lado_x × Lado_y × Altura × Cantidad",
    "Acero longitudinal (a lo alto) + estribos por sección × (1 + 5% desperdicio)",
  ],
  placas: [
    "Concreto (m³) = Largo × Ancho (espesor) × Alto × Cantidad",
    "Acero vertical (a lo alto) + horizontal (a lo largo), doble cortina × (1 + 5% desperdicio)",
    "Encofrado (m²) = 2 caras × Largo × Alto × Cantidad",
  ],
  vigas: [
    "Concreto (m³) = Largo × Ancho × Alto × Cantidad",
    "Acero longitudinal (esquina + intermedio) + estribos × (1 + 5% desperdicio)",
  ],
  techo: [
    "Concreto neto (m³) = Área × coef(espesor)   ·   0.17→0.08 · 0.20→0.0875 · 0.25→0.10 m³/m²",
    "Ladrillos (und) = Área × 8.33 und/m²   ·   Viguetas @ 0.40 m",
    "Acero (kg) = acero de viguetas (positivo + negativo) + acero de temperatura (1/4\" @ 0.25)",
    "Acero total = (viguetas + temperatura) × (1 + 5% desperdicio)",
  ],
  escalera: ["Concreto (m³) = Largo × Ancho × Alto × Cantidad", "Acero en malla X-X y Y-Y × (1 + 5% desperdicio)"],
  cisterna: ["Concreto (m³) = Largo × Ancho × Alto × Cantidad", "Acero en malla X-X y Y-Y × (1 + 5% desperdicio)"],
};

const LABELS = {
  zapatas: "Zapatas", viga_cimentacion: "Viga de Cimentación", cimiento_corrido: "Cimiento Corrido",
  sobrecimiento: "Sobrecimiento", columnas: "Columnas", placas: "Placas", vigas: "Vigas",
  techo: "Losa Aligerada", escalera: "Escalera", cisterna: "Cisterna",
};

function aceroInputs(el) {
  const rows = [];
  if (el.aceroXX) rows.push(["Acero eje X-X", `${el.aceroXX.fierro} · ${el.aceroXX.nelem} und · @ ${n2(distNum(el.aceroXX.dist))} m`]);
  if (el.aceroYY) rows.push(["Acero eje Y-Y", `${el.aceroYY.fierro} · ${el.aceroYY.nelem} und · @ ${n2(distNum(el.aceroYY.dist))} m`]);
  if (el.esquina) rows.push(["Acero long. esquina", `${el.esquina.fierro} · ${el.esquina.nelem} und`]);
  if (el.intermedio) rows.push(["Acero long. intermedio", `${el.intermedio.fierro} · ${el.intermedio.nelem} und`]);
  if (el.estribo) rows.push(["Estribo", `${el.estribo.fierro} · ${el.estribo.nelem} und`]);
  if (el.positivo) rows.push(["Acero positivo", `${el.positivo.fierro} · ${el.positivo.nelem} und/vigueta`]);
  if (el.negativo) rows.push(["Acero negativo", `${el.negativo.fierro} · ${el.negativo.nelem} und/vigueta`]);
  if (el.temperatura) rows.push(["Acero de temperatura", `${el.temperatura.fierro} @ 0.25 m`]);
  if (el.espesor !== undefined) rows.push(["Espesor de losa", `${n2(el.espesor)} m`]);
  return rows;
}

export function generarMemoriaCalculo({ el, familyKey, params = {}, ctx = {}, delta = null, losa = null, historial = null, resumen = null }) {
  if (!el) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const fam = LABELS[familyKey] || familyKey;

  // Sello / cabecera ConstruLink · COREX
  doc.setFillColor(...PETROL);
  doc.rect(0, 0, W, 26, "F");
  doc.setFillColor(...TEAL);
  doc.rect(0, 26, W, 1.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold"); doc.setFontSize(15);
  doc.text("CONSTRULINK · COREX", 14, 12);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text("Núcleo de Ingeniería · Memoria de Cálculo Estructural", 14, 19);
  doc.setFontSize(8);
  doc.text(new Date().toLocaleString("es-PE"), W - 14, 12, { align: "right" });
  doc.text("ADR-COREX-001 · Motor determinista", W - 14, 18, { align: "right" });

  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text(`MEMORIA DE CÁLCULO · ${fam.toUpperCase()}`, 14, 38);
  doc.setFontSize(10); doc.setFont("helvetica", "normal");
  doc.text(`Elemento: ${el.codigo || "—"} · ${el.ubicacion || ""}`, 14, 45);

  // Datos del proyecto
  autoTable(doc, {
    startY: 50,
    head: [["DATOS DEL PROYECTO", ""]],
    body: [
      ["Ancho (frente)", `${n2(ctx.ancho)} m`],
      ["Largo (fondo)", `${n2(ctx.largo)} m`],
      ["Pisos", `${ctx.pisos ?? "—"}`],
      ["Altura 1er piso", `${n2(ctx.altura1)} m`],
      ["f'c / fy", `${n2(params.fc)} / ${n2(params.fy)} kg/cm²`],
      ["Recubrimiento", `${n2((params.recubrimiento || 0) * 100)} cm`],
    ],
    theme: "grid", headStyles: { fillColor: PETROL, fontSize: 9 }, bodyStyles: { fontSize: 8.5 },
    columnStyles: { 0: { cellWidth: 60, fontStyle: "bold" } }, margin: { left: 14, right: 14 },
  });

  // Inputs del usuario
  const dimRows = [
    ["Dimensiones (L×A×H)", `${n2(el.largo)} × ${n2(el.ancho)} × ${n2(el.alto)} m`],
    ["Cantidad", `${n2(el.cantidad)} und`],
    ...aceroInputs(el),
  ];
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 4,
    head: [["INPUTS DEL USUARIO", ""]],
    body: dimRows, theme: "grid", headStyles: { fillColor: TEAL, fontSize: 9 }, bodyStyles: { fontSize: 8.5 },
    columnStyles: { 0: { cellWidth: 60, fontStyle: "bold" } }, margin: { left: 14, right: 14 },
  });

  // Fórmulas aplicadas
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 4,
    head: [["FÓRMULAS APLICADAS POR EL MOTOR COREX"]],
    body: (FORMULAS[familyKey] || []).map((f) => [f]),
    theme: "grid", headStyles: { fillColor: PETROL, fontSize: 9 }, bodyStyles: { fontSize: 8.5, font: "courier" },
    margin: { left: 14, right: 14 },
  });

  // Resultado del motor
  const resBody = [
    ["Concreto", `${n2(el.concreto)} m³`],
    ["Acero de refuerzo", `${n2(el.acero)} kg`],
  ];
  if (losa) {
    resBody.push(["Concreto neto (e=" + n2(losa.espesor_m) + " m)", `${n2(losa.concreto_m3)} m³ (${losa.coef_concreto_m3_m2} m³/m²)`]);
    resBody.push(["Ladrillos de techo", `${losa.ladrillos_und} und`]);
    resBody.push(["Acero de viguetas", `${n2(losa.acero_viguetas_kg)} kg`]);
    resBody.push(["Acero de temperatura", `${n2(losa.acero_temperatura_kg)} kg`]);
    resBody.push(["Acero total (+5%)", `${n2(losa.acero_kg)} kg`]);
  }
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 4,
    head: [["RESULTADO DEL MOTOR", ""]],
    body: resBody, theme: "grid", headStyles: { fillColor: [22, 163, 74], fontSize: 9 },
    bodyStyles: { fontSize: 9, fontStyle: "bold" }, columnStyles: { 0: { cellWidth: 70 } },
    margin: { left: 14, right: 14 },
  });

  // Delta aplicado al presupuesto (trazabilidad)
  if (delta && (Math.abs(delta.concreto || 0) >= 0.01 || Math.abs(delta.acero || 0) >= 0.01)) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 4,
      head: [["Δ APLICADO AL PRESUPUESTO (trazabilidad Delta-Baseline)", ""]],
      body: [
        ["Δ Concreto vs baseline", `${(delta.concreto >= 0 ? "+" : "")}${n2(delta.concreto)} m³`],
        ["Δ Acero vs baseline", `${(delta.acero >= 0 ? "+" : "")}${n2(delta.acero)} kg`],
      ],
      theme: "grid", headStyles: { fillColor: [180, 83, 9], fontSize: 9 }, bodyStyles: { fontSize: 8.5 },
      columnStyles: { 0: { cellWidth: 90, fontStyle: "bold" } }, margin: { left: 14, right: 14 },
    });
  }

  // Resumen de cambios vs diseño base (justificación del nuevo presupuesto)
  if (Array.isArray(historial) && historial.length) {
    const body = [];
    historial.slice(0, 14).forEach((a) => {
      const lbl = (a.cambios || []).map((c) => `${c.label}: ${c.de} → ${c.a}`).join("  |  ") || "Cambio de diseño";
      body.push([new Date(a.ts).toLocaleString("es-PE"), lbl, `${a.impacto >= 0 ? "+" : ""}${n2(a.impacto)}`, n2(a.total)]);
    });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 4,
      head: [["RESUMEN DE CAMBIOS vs DISEÑO BASE (justificación del presupuesto)"]],
      body: [[""]], theme: "plain", headStyles: { fillColor: [12, 43, 56], textColor: 255, fontSize: 9 },
      bodyStyles: { minCellHeight: 0.1 }, margin: { left: 14, right: 14 },
    });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY,
      head: [["Fecha", "Elemento / Campo (antes → después)", "Impacto S/", "Total S/"]],
      body, theme: "grid", headStyles: { fillColor: [180, 83, 9], fontSize: 8 },
      bodyStyles: { fontSize: 7.5 }, columnStyles: { 2: { halign: "right" }, 3: { halign: "right" } },
      margin: { left: 14, right: 14 },
    });
    if (resumen && resumen.total != null) {
      const dev = ((resumen.total - resumen.controlTotal) / resumen.controlTotal) * 100;
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 2,
        body: [
          ["Presupuesto base (Santisteban)", `S/ ${n2(resumen.controlTotal)}`],
          ["Presupuesto actual", `S/ ${n2(resumen.total)}`],
          ["Desvío acumulado", `${dev >= 0 ? "+" : ""}${n2(dev)} %`],
        ],
        theme: "grid", bodyStyles: { fontSize: 8.5, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 90 }, 1: { halign: "right" } }, margin: { left: 14, right: 14 },
      });
    }
  }

  // Bloque de firma
  let y = doc.lastAutoTable.finalY + 22;
  if (y > 260) { doc.addPage(); y = 40; }
  doc.setDrawColor(120); doc.setLineWidth(0.3);
  doc.line(24, y, 90, y);
  doc.line(W - 90, y, W - 24, y);
  doc.setFontSize(9); doc.setTextColor(30);
  doc.text("Ing. Santisteban", 57, y - 2, { align: "center" });
  doc.setFontSize(8.5); doc.setTextColor(60);
  doc.text("Ingeniero Responsable", 57, y + 5, { align: "center" });
  doc.text("CIP N° ____________", 57, y + 10, { align: "center" });
  doc.text("Revisado / Aprobado", W - 57, y + 5, { align: "center" });
  doc.text("ConstruLink · COREX", W - 57, y + 10, { align: "center" });

  doc.setFontSize(7); doc.setTextColor(150);
  doc.text("Documento generado por el motor determinista COREX (ADR-COREX-001). El frontend no calcula.", 14, 288);

  doc.save(`Memoria_Calculo_${fam.replace(/\s+/g, "_")}_${el.codigo || "elemento"}.pdf`);
}
